import { commonUtils } from '@/helpers/commonUtils'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import style from './PageFooter.module.less'
import { useTranslationWithType } from '@/i18n/i18n'

export type ProductItemType = 'web' | 'pdf' | 'video' | 'image'
export type ProductItemClickListener = (item: ProductItemType) => void

export type ResourceItemType = 'price' | 'contact'
export type ResourceItemClickListener = (item: ResourceItemType) => void

export type LegalItemType = 'privacy' | 'terms'
export type LegalItemClickListener = (item: LegalItemType) => void

export type ToolItemType =
  | '/es/ai-pdf-translator'
  | '/ru/ai-pdf-translator'
  | '/pt/ai-pdf-translator'
  | '/id/ai-pdf-translator'
  | '/cs/ai-pdf-translator'
  | '/de/ai-pdf-translator'
  | '/fr/ai-pdf-translator'
  | '/it/ai-pdf-translator'
  | '/nl/ai-pdf-translator'
  | '/pl/ai-pdf-translator'
  | '/vi/ai-pdf-translator'
  | '/tr/ai-pdf-translator'
  | '/uk/ai-pdf-translator'
  | '/zh-Hant/ai-pdf-translator'
  | '/ja/ai-pdf-translator'
  | '/ko/ai-pdf-translator'
  | '/ar/ai-pdf-translator'
  | '/fa/ai-pdf-translator'
  | '/th/ai-pdf-translator'
  | '/hi/ai-pdf-translator'
  | '/en/ai-pdf-translator'
  | '/zh/ai-pdf-summarizer'
export type ToolItemClickListener = (item: ToolItemType) => void
/** PageFooter 组件的 Props */
export type PageFooterProps = {
  onProductItemClick?: ProductItemClickListener
  onResourcesItemClick?: ResourceItemClickListener
  onLegalItemClick?: LegalItemClickListener
  onToolItemClick?: ToolItemClickListener
}

const PageFooter: FC<PageFooterProps> = observer((props: PageFooterProps) => {
  const { onProductItemClick, onResourcesItemClick, onLegalItemClick, onToolItemClick } = props

  const { t, i18n } = useTranslationWithType() // 获取 i18n 实例
  const currentLocale = i18n.language; // 获取当前语言

  // 定义语言到路径的映射
  const localeToToolPath: Record<string, ToolItemType | undefined> = {
    'es': '/es/ai-pdf-translator',
    'ru': '/ru/ai-pdf-translator',
    'pt': '/pt/ai-pdf-translator',
    'id': '/id/ai-pdf-translator',
    'cs': '/cs/ai-pdf-translator',
    'de': '/de/ai-pdf-translator',
    'fr': '/fr/ai-pdf-translator',
    'it': '/it/ai-pdf-translator',
    'nl': '/nl/ai-pdf-translator',
    'pl': '/pl/ai-pdf-translator',
    'vi': '/vi/ai-pdf-translator',
    'tr': '/tr/ai-pdf-translator',
    'uk': '/uk/ai-pdf-translator',
    'zh-Hant': '/zh-Hant/ai-pdf-translator',
    'ja': '/ja/ai-pdf-translator',
    'ko': '/ko/ai-pdf-translator',
    'ar': '/ar/ai-pdf-translator',
    'fa': '/fa/ai-pdf-translator',
    'th': '/th/ai-pdf-translator',
    'hi': '/hi/ai-pdf-translator',
    'en': '/en/ai-pdf-translator',
    'zh': '/zh/ai-pdf-summarizer',
  };

  const toolPath = localeToToolPath[currentLocale] || '/en/ai-pdf-translator'; // 默认英文路径

  return (
    <div className={classNames(style.pageFooter)}>
      <div className={classNames(style.footerContent)}>
        <div className={classNames(style.left)}>
          <img className={classNames(style.logo)} src={commonUtils.getStaticFile('logo.png')} />
          <p className={classNames(style.text)}>{t('slogen')}</p>
          {/* <p className={classNames(style.text)}>{t('pageFooter.owner')}</p>
          <p className={classNames(style.icp)}>{t('pageFooter.icp')}</p> */}
        </div>
        <div className={classNames(style.right, 'flex-row', 'align-right', 'justify-start')}>
          <div className={classNames(style.box, 'mr-88')}>
            <p className={classNames(style.title)}>{t('pageFooter.tools.title')}</p>
            <a
              href={toolPath}
              onClick={(e) => {
                if (onToolItemClick) {
                  e.preventDefault()
                  onToolItemClick(toolPath)
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.tools.pdf')}
            </a>
          </div>
          <div className={classNames(style.box, 'mr-88')}>
            <p className={classNames(style.title)}>{t('pageFooter.product.title')}</p>
            <a
              href='/?to=web'
              onClick={(e) => {
                if (onProductItemClick) {
                  e.preventDefault()
                  onProductItemClick('web')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.product.web')}
            </a>
            <a
              href='/?to=pdf'
              onClick={(e) => {
                if (onProductItemClick) {
                  e.preventDefault()
                  onProductItemClick('pdf')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.product.pdf')}
            </a>
            <a
              href='/?to=video'
              onClick={(e) => {
                if (onProductItemClick) {
                  e.preventDefault()
                  onProductItemClick('video')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.product.video')}
            </a>
            <a
              href='/?to=image'
              onClick={(e) => {
                if (onProductItemClick) {
                  e.preventDefault()
                  onProductItemClick('image')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.product.image')}
            </a>
          </div>
          <div className={classNames(style.box, 'mr-83')}>
            <p className={classNames(style.title)}>{t('pageFooter.resources.title')}</p>
            <a
              href='/pricing'
              onClick={(e) => {
                if (onResourcesItemClick) {
                  e.preventDefault()
                  onResourcesItemClick('price')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageHeader.pricing')}
            </a>
            {/* <a href='/' className={classNames(style.link)}>
              {t('pageHeader.help')}
            </a> */}
            <a
              href='/contact'
              onClick={(e) => {
                if (onResourcesItemClick) {
                  e.preventDefault()
                  onResourcesItemClick('contact')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageHeader.contact')}
            </a>
          </div>
          {/* <div className={classNames(style.box, 'mr-88')}>
            <p className={classNames(style.title)}>{t('pageFooter.follow.title')}</p>
            <a href='https://twitter.com/' className={classNames(style.link)}>
              {t('pageFooter.follow.twitter')}
            </a>
            <a href='https://www.facebook.com/' className={classNames(style.link)}>
              {t('pageFooter.follow.facebook')}
            </a>
          </div> */}
          <div className={classNames(style.box)}>
            <p className={classNames(style.title)}>{t('pageFooter.legal.title')}</p>
            <a
              href='/policy'
              target='_black'
              onClick={(e) => {
                if (onLegalItemClick) {
                  e.preventDefault()
                  onLegalItemClick('privacy')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.legal.policy')}
            </a>
            <a
              href='/terms'
              target='_black'
              onClick={(e) => {
                if (onLegalItemClick) {
                  e.preventDefault()
                  onLegalItemClick('terms')
                }
              }}
              className={classNames(style.link)}
            >
              {t('pageFooter.legal.terms')}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
})

export default PageFooter
